import React, { useState, useEffect } from 'react';
import { PageSection, SiteData, YachtItem, YachtReservation, YachtAvailability } from '../../../types';
import { dbService } from '../../../services/firebase';
import { Calendar as CalendarIcon, CheckCircle2, XCircle, ArrowRight, ShieldCheck, Anchor, Plus } from 'lucide-react';
import { isDarkBackground, getOptimalTextColor, getOptimalMutedTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
  onSelectYacht?: (yacht: YachtItem) => void;
}

export const YachtAvailabilityCalendarSection: React.FC<SectionProps> = ({
  section,
  siteData,
  onNavigate,
  onSelectYacht,
}) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'CANLI MÜSAİTLİK DURUMU';
  const title = content.title || 'Müsaitlik ve Doluluk Takvimi';
  const description =
    content.description ||
    'Filomuzdaki teknelerin güncel doluluk takvimini inceleyin, uygun tarih aralığı için anında rezervasyon talebinizi iletin.';

  const [yachts, setYachts] = useState<YachtItem[]>([]);
  const [selectedYachtId, setSelectedYachtId] = useState<string>('');
  const [reservations, setReservations] = useState<YachtReservation[]>([]);
  const [availabilities, setAvailabilities] = useState<YachtAvailability[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    const fetchFleetAndReservations = async () => {
      try {
        setIsLoading(true);
        const [fleet, resList, availList] = await Promise.all([
          dbService.getYachts(siteData.id),
          dbService.getReservations(siteData.id),
          dbService.getYachtAvailability(siteData.id),
        ]);

        if (isMounted) {
          const validYachts = fleet || [];
          setYachts(validYachts);
          if (validYachts.length > 0) {
            setSelectedYachtId(validYachts[0].id);
          }
          setReservations(resList || []);
          setAvailabilities(availList || []);
        }
      } catch (err) {
        console.warn('Availability calendar fetch error:', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchFleetAndReservations();
    return () => {
      isMounted = false;
    };
  }, [siteData.id]);

  const activeYacht = yachts.find((y) => y.id === selectedYachtId);

  const bgColor = style.backgroundColor || '#0F172A';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const mutedTextColor = getOptimalMutedTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#0284C7';

  // Generate 21 upcoming days using strictly real reservations and availability records
  const days = Array.from({ length: 21 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i + 1);
    const dateStr = d.toISOString().split('T')[0];

    // Real check: Is this yacht booked or blocked on this date?
    const hasActiveReservation = reservations.some(
      (r) =>
        !r.isDeleted &&
        r.yachtId === selectedYachtId &&
        (r.status === 'CONFIRMED' || r.status === 'COMPLETED') &&
        r.startDate <= dateStr &&
        r.endDate >= dateStr
    );

    const hasActiveBlock = availabilities.some(
      (a) =>
        !a.isDeleted &&
        a.yachtId === selectedYachtId &&
        a.status !== 'AVAILABLE' &&
        a.startDate <= dateStr &&
        a.endDate >= dateStr
    );

    const isBooked = hasActiveReservation || hasActiveBlock;

    return {
      day: d.getDate(),
      month: d.toLocaleDateString('tr-TR', { month: 'short' }),
      weekday: d.toLocaleDateString('tr-TR', { weekday: 'short' }),
      dateStr,
      isBooked,
    };
  });

  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-8 select-none relative overflow-hidden"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-6xl mx-auto space-y-10 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          {badge && (
            <div
              className={`inline-block px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${
                isDark
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'bg-cyan-500/10 text-cyan-700 border border-cyan-500/20'
              }`}
            >
              {badge}
            </div>
          )}
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight">{title}</h2>
          {description && (
            <p className="text-sm sm:text-base leading-relaxed max-w-2xl mx-auto" style={{ color: mutedTextColor }}>
              {description}
            </p>
          )}
        </div>

        {/* Empty Fleet State */}
        {isLoading ? (
          <div className="py-16 text-center text-slate-400">
            <div className="inline-block w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-xs font-medium">Müsaitlik takvimi yükleniyor...</p>
          </div>
        ) : yachts.length === 0 ? (
          <div
            className={`max-w-xl mx-auto p-8 rounded-3xl border text-center space-y-4 ${
              isDark ? 'bg-slate-900/60 border-white/10 text-slate-300' : 'bg-white border-slate-200 text-slate-600 shadow-sm'
            }`}
          >
            <div className="w-14 h-14 rounded-2xl bg-teal-500/10 text-teal-600 flex items-center justify-center mx-auto">
              <Anchor className="w-7 h-7" />
            </div>
            <div className="space-y-1.5">
              <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Takvimde Gösterilecek Yat Bulunamadı
              </h3>
              <p className="text-xs leading-relaxed max-w-md mx-auto text-slate-400">
                Filonuzda henüz kayıtlı bir tekne bulunmadığı için canlı müsaitlik takvimi görüntülenemiyor.
              </p>
            </div>
            {onNavigate && (
              <button
                type="button"
                onClick={() => onNavigate('#yacht-fleet')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-xl text-xs font-bold transition shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Yat Yönetimine Git</span>
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Yacht Selector Tabs */}
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {yachts.map((y) => (
                <button
                  key={y.id}
                  type="button"
                  onClick={() => setSelectedYachtId(y.id)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                    selectedYachtId === y.id
                      ? 'shadow-lg scale-105'
                      : isDark
                      ? 'bg-slate-800/80 hover:bg-slate-800 text-slate-300 border border-slate-700'
                      : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'
                  }`}
                  style={
                    selectedYachtId === y.id
                      ? { backgroundColor: accentColor, color: getOptimalTextColor(accentColor) }
                      : undefined
                  }
                >
                  <span>{y.name}</span>
                  <span className="text-[10px] opacity-75">
                    ({y.currency === 'USD' ? '$' : y.currency === 'TRY' ? '₺' : '€'}
                    {(y.pricePerDay || 0).toLocaleString()}/gün)
                  </span>
                </button>
              ))}
            </div>

            {/* Selected Yacht Summary & Calendar Grid */}
            <div
              className={`border rounded-3xl p-6 sm:p-8 backdrop-blur-md shadow-2xl space-y-6 ${
                isDark
                  ? 'bg-slate-900/90 border-slate-800 text-white'
                  : 'bg-white border-slate-200 text-slate-900'
              }`}
            >
              <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                <div>
                  <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-cyan-400' : 'text-blue-600'}`}>
                    Seçili Tekne
                  </span>
                  <h3 className={`text-xl sm:text-2xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {activeYacht?.name}
                  </h3>
                  <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {activeYacht?.type} • {activeYacht?.lengthMeters || activeYacht?.length || '-'} Metre • {activeYacht?.capacity} Kişi Kapasitesi • Bağlı Liman: {activeYacht?.portLocation || activeYacht?.basePort || 'Belirtilmemiş'}
                  </p>
                </div>

                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-semibold">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Müsait</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400 font-semibold">
                    <XCircle className="w-4 h-4" />
                    <span>Dolu / Rezerve</span>
                  </div>
                </div>
              </div>

              {/* Days Grid */}
              <div className="grid grid-cols-3 sm:grid-cols-7 gap-3">
                {days.map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-3.5 rounded-2xl border text-center transition flex flex-col justify-between h-24 ${
                      item.isBooked
                        ? isDark
                          ? 'bg-rose-950/30 border-rose-900/40 text-rose-300 cursor-not-allowed'
                          : 'bg-rose-50 border-rose-200 text-rose-700 cursor-not-allowed'
                        : isDark
                        ? 'bg-slate-800/60 border-slate-700/60 hover:border-cyan-500 text-slate-200 cursor-pointer hover:scale-105'
                        : 'bg-slate-50 border-slate-200 hover:border-blue-500 text-slate-800 cursor-pointer hover:scale-105'
                    }`}
                    onClick={() => {
                      if (!item.isBooked) {
                        if (activeYacht && onSelectYacht) {
                          onSelectYacht(activeYacht);
                        } else if (onNavigate) {
                          onNavigate(`/rezervasyon?yachtId=${activeYacht?.id}&date=${item.dateStr}`);
                        }
                      }
                    }}
                  >
                    <span className={`text-[10px] uppercase font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item.weekday}</span>
                    <span className={`text-lg font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.day} {item.month}</span>
                    <span
                      className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded-md ${
                        item.isBooked
                          ? isDark
                            ? 'bg-rose-900/40 text-rose-300'
                            : 'bg-rose-100 text-rose-700'
                          : isDark
                          ? 'bg-emerald-900/40 text-emerald-300'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {item.isBooked ? 'Dolu' : 'Müsait'}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Footer */}
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  <ShieldCheck className="w-4 h-4 text-cyan-500 shrink-0" />
                  <span>Tarihler gerçek filo ve rezervasyon verilerine göre anlık olarak güncellenir.</span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (activeYacht && onSelectYacht) {
                      onSelectYacht(activeYacht);
                    } else if (onNavigate) {
                      onNavigate('/rezervasyon');
                    }
                  }}
                  className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold text-xs transition flex items-center justify-center gap-2 shadow-lg cursor-pointer"
                  style={{ backgroundColor: accentColor, color: getOptimalTextColor(accentColor) }}
                >
                  <CalendarIcon className="w-4 h-4" />
                  <span>Rezervasyon Talebi Gönder</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
